import { useEffect, useRef, useState } from 'react';
import { Layers, AlertTriangle, Check, Play, Pause, Loader2 } from 'lucide-react';
import { api, phoneNumbersAPI, voicesAPI } from '../services/api.js';
import { formatPhone } from '../lib/format.js';

/**
 * WHICH TECHNOLOGY ANSWERS THIS NUMBER.
 *
 * Brian, 2026-09-23: "we need to have a selector in the settings area of AlfredVoice to
 * stipulate which technology stack we're going to start with… we need to have the ability
 * to select the voice."
 *
 * ⛔ THIS SCREEN HOLDS NO COPY OF WHAT THE STACKS CAN DO. The labels, the voice list and the
 * capability matrix all come from GET /voice-stacks, which core builds from
 * services/voiceStacks.js. A screen with its own hard-coded matrix is how a dashboard ends
 * up promising a warm transfer on a line that has no transfer tool wired — the recurring
 * bug on this platform is exactly a frontend and a backend disagreeing about a setting.
 *
 * ⛔ AND IT SHOWS WHAT IS LOST BEFORE THE SAVE, NOT AFTER. Moving a number to realtime
 * removes transfers, take-a-message and outbound. That has to be on screen while the choice
 * is being made, or someone moves a customer's main line and finds out from a caller.
 */
/**
 * Hear a voice before putting it on a customer's line.
 *
 * ⛔ xAI IS THE ONE VENDOR THAT CANNOT RETURN AN MP3 — it speaks only over a websocket, and
 * only in PCM, so core wraps its sample in a WAV header. That is core's problem, not this
 * button's: it goes through the same vendor-aware preview path every other voice uses and
 * plays whatever comes back.
 *
 * Owns its own <audio> and object URL, and frees both on unmount or when the voice changes —
 * a leaked blob URL holds the whole sample in memory for the life of the page.
 */
function VoicePreviewButton({ voice }) {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState('');
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  const stop = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    setPlaying(false);
  };

  useEffect(() => stop, []);          // unmount
  useEffect(() => { stop(); setError(''); }, [voice]);

  async function toggle() {
    if (playing) return stop();
    if (!voice) return;
    setLoading(true);
    setError('');
    try {
      const url = await voicesAPI.previewBlobUrl('xai', voice);
      urlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = stop;
      audio.onerror = () => { setError('Playback failed.'); stop(); };
      await audio.play();
      setPlaying(true);
    } catch (e) {
      // The first play of a voice synthesizes it, so this is where a missing key or a
      // vendor outage shows up. Say which, rather than just failing silently.
      setError(e?.message || 'Could not play that voice.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={!voice || loading}
      title={error || (voice ? `Hear ${voice}` : 'Pick a voice first')}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition
        ${error ? 'border-red-500/40 text-red-300'
                : 'border-stone-700 text-stone-300 hover:border-alfred-gold hover:text-alfred-gold'}
        disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {loading ? <Loader2 size={14} className="animate-spin" />
               : playing ? <Pause size={14} /> : <Play size={14} />}
      {loading ? 'Loading…' : playing ? 'Stop' : 'Listen'}
    </button>
  );
}

export default function CallStackCard() {
  const [numbers, setNumbers] = useState([]);
  const [defs, setDefs] = useState(null);
  const [draft, setDraft] = useState({});        // id -> {voiceStack, realtimeVoice, realtimeFallback}
  const [saving, setSaving] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Whether the box honours this setting at all (core answers it), and whether any number
  // here has never been set. The second one matters on its own — see the banner below.
  const [enforced, setEnforced] = useState(true);
  const [anyUnset, setAnyUnset] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [list, stacks] = await Promise.all([
        phoneNumbersAPI.list(),
        api.get('/voice-stacks')
      ]);
      setNumbers(list?.data || []);
      setDefs(stacks?.data || null);
      setEnforced((list?.data || []).every((n) => n.stack?.enforced !== false));
      setAnyUnset((list?.data || []).some((n) => n.stack?.source === 'default'));
    } catch (e) {
      // An older core has no /voice-stacks. Say that plainly rather than rendering an
      // empty selector that silently writes nothing.
      setError(e?.status === 404
        ? 'This dashboard is newer than the server — the stack selector needs the latest core deploy.'
        : (e?.message || 'Could not load the call stacks.'));
    } finally {
      setLoading(false);
    }
  }

  // A number saved before this feature existed has no `stack`; it is on A, like every
  // number that predates the selector.
  const current = (n) => ({
    voiceStack: draft[n.id]?.voiceStack ?? n.stack?.id ?? 'A',
    realtimeVoice: draft[n.id]?.realtimeVoice ?? n.stack?.realtimeVoice ?? '',
    realtimeName: draft[n.id]?.realtimeName ?? n.stack?.realtimeName ?? '',
    messageNotifyNumber: draft[n.id]?.messageNotifyNumber
      ?? n.stack?.messageNotifyNumber ?? '',
    realtimeFallback: draft[n.id]?.realtimeFallback ?? n.stack?.realtimeFallback ?? true
  });

  const dirty = (n) => {
    const c = current(n);
    return c.voiceStack !== (n.stack?.id ?? 'A')
      || (c.realtimeVoice || null) !== (n.stack?.realtimeVoice ?? null)
      || (c.realtimeName.trim() || null) !== (n.stack?.realtimeName ?? null)
      || (c.messageNotifyNumber.trim() || null) !== (n.stack?.messageNotifyNumber ?? null)
      || c.realtimeFallback !== (n.stack?.realtimeFallback ?? true);
  };

  const set = (id, patch) =>
    setDraft((d) => ({ ...d, [id]: { ...current(numbers.find((n) => n.id === id)), ...patch } }));

  async function save(n) {
    setSaving(n.id);
    setError('');
    const c = current(n);
    try {
      const payload = { voiceStack: c.voiceStack };
      if (c.voiceStack === 'B') {
        // Only send a voice when one is chosen — core rejects an empty string, and an
        // unset voice is a legitimate state (xAI uses its default).
        if (c.realtimeVoice) payload.realtimeVoice = c.realtimeVoice;
        // Always sent, because '' is how the field is CLEARED — omitting it would make an
        // erased name look like "no change" and silently keep the old one.
        payload.realtimeName = c.realtimeName.trim();
        payload.messageNotifyNumber = c.messageNotifyNumber.trim();
        payload.realtimeFallback = c.realtimeFallback;
      }
      const out = await phoneNumbersAPI.update(n.id, payload);
      setNumbers((list) => list.map((x) => (x.id === n.id ? (out?.data || x) : x)));
      setDraft((d) => { const { [n.id]: _drop, ...rest } = d; return rest; });
      setSavedAt(new Date());
    } catch (e) {
      setError(e?.status === 403
        ? 'Only an admin can change which stack a number answers on.'
        : (e?.message || 'Could not save that.'));
    } finally {
      setSaving(null);
    }
  }

  const stackDef = (id) => (defs?.stacks || []).find((s) => s.id === id) || null;

  return (
    <section className="mb-8 rounded-xl border border-stone-800 bg-stone-900/40 p-5">
      <header className="flex items-center justify-between gap-4 mb-1">
        <div className="flex items-center gap-2">
          <Layers size={18} className="text-alfred-gold" />
          <h2 className="text-xl text-stone-100">Call stack</h2>
        </div>
        {savedAt && (
          <span className="text-xs text-stone-500">Saved {savedAt.toLocaleTimeString()}</span>
        )}
      </header>

      <p className="text-sm text-stone-400 mb-4">
        Which technology answers each number. The standard stack does everything; the realtime
        stack answers noticeably faster and can look things up, remember callers and take
        messages &mdash; but it cannot transfer a call or dial out.
      </p>

      {/* ⛔ THIS PAGE CANNOT SEE A BOX-LEVEL OVERRIDE. The box has its own list that forces
          certain numbers onto realtime, and core has no way to know. For a number nobody has
          set here, "Standard" is the platform default — not an observed fact — and rendering
          it confidently would promise a warm transfer on a line that may have two tools.
          Says so only while some number is still unset, so it disappears as they get set. */}
      {!loading && enforced && anyUnset && (
        <p className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-stone-200">
          <strong>Some numbers below have never been set here</strong> and are showing the
          platform default. If one is being forced onto realtime by a setting on the call box,
          this page cannot see it. Set the number explicitly to be sure — or check the box log
          line <code className="text-amber-600">PANEL STACK:</code>, which records what actually
          answered.
        </p>
      )}

      {!loading && !enforced && (
        <p className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-stone-200">
          <strong>Recorded, not yet live.</strong> The call box is not reading this setting, so
          saving here does not move a line today.
        </p>
      )}

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : numbers.length === 0 ? (
        <p className="text-sm text-stone-500">No phone numbers on this account yet.</p>
      ) : (
        <ul className="divide-y divide-stone-900">
          {numbers.map((n) => {
            const c = current(n);
            const def = stackDef(c.voiceStack);
            const lost = def?.lost || [];
            return (
              <li key={n.id} className="py-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-stone-100 font-medium">
                      {formatPhone(n.phoneNumber)}
                      {n.configuration?.label && (
                        <span className="ml-2 text-xs text-stone-400 italic">— {n.configuration.label}</span>
                      )}
                    </div>
                    <div className="text-xs text-stone-500">{def?.summary || ''}</div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {(defs?.stacks || []).map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => set(n.id, { voiceStack: s.id })}
                        className={`rounded-lg px-3 py-1.5 text-sm border transition ${
                          c.voiceStack === s.id
                            ? 'border-alfred-gold text-alfred-gold bg-alfred-gold/15'
                            : 'border-stone-700 text-stone-400 hover:text-stone-200'
                        }`}
                      >
                        {c.voiceStack === s.id && <Check size={13} className="inline mr-1 -mt-0.5" />}
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>

                {c.voiceStack === 'B' && (
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div className="text-sm">
                      <span className="block text-xs text-stone-400 mb-1">Voice</span>
                      <div className="flex items-center gap-2">
                        <select
                          className="flex-1 min-w-0 rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 text-sm text-stone-100"
                          value={c.realtimeVoice}
                          onChange={(e) => set(n.id, { realtimeVoice: e.target.value })}
                        >
                          <option value="">xAI default</option>
                          {(defs?.realtimeVoices || []).map((v) => (
                            <option key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</option>
                          ))}
                        </select>
                        <VoicePreviewButton voice={c.realtimeVoice} />
                      </div>
                    </div>

                    {/* ⛔ THIS IS A NUMBER THE PLATFORM WILL DIAL, so it lives here in
                        configuration and nowhere else. The caller gives their own callback
                        number to the AI; that one is stored and shown and never dialled. If a
                        caller could influence what gets rung, anyone who phoned the line could
                        make Alfred call anyone they liked with a message they dictated. */}
                    <label className="text-sm sm:col-span-2">
                      <span className="block text-xs text-stone-400 mb-1">
                        Call this number when a message is taken
                      </span>
                      <input
                        type="tel"
                        className="w-full rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 text-sm text-stone-100"
                        placeholder="+15125550123 — leave blank for no call"
                        value={c.messageNotifyNumber}
                        maxLength={24}
                        onChange={(e) => set(n.id, { messageNotifyNumber: e.target.value })}
                      />
                      <span className="block text-xs text-stone-500 mt-1">
                        After the call ends, the AI rings this number and reads the message out,
                        including the caller&rsquo;s own number. The message also appears on the
                        call and in your recap email, so nothing is lost if the call is missed.
                      </span>
                    </label>

                    {/* ⛔ THE NAME AND THE VOICE HAVE TO BE THE SAME PERSON, which is why this
                        sits next to the picker rather than somewhere else in Settings. Without
                        it the line names itself from the tenant's first persona row — on 346
                        that meant "Jameson" spoken in a female voice to real callers. */}
                    <label className="text-sm">
                      <span className="block text-xs text-stone-400 mb-1">Name it gives itself</span>
                      <input
                        type="text"
                        className="w-full rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 text-sm text-stone-100"
                        placeholder={c.realtimeVoice
                          ? c.realtimeVoice[0].toUpperCase() + c.realtimeVoice.slice(1)
                          : 'e.g. Vicky'}
                        value={c.realtimeName}
                        maxLength={60}
                        onChange={(e) => set(n.id, { realtimeName: e.target.value })}
                      />
                      <span className="block text-xs text-stone-500 mt-1">
                        Spoken on the call — &ldquo;{(c.realtimeName.trim()
                          || (c.realtimeVoice
                              ? c.realtimeVoice[0].toUpperCase() + c.realtimeVoice.slice(1)
                              : 'Alfred'))}, with Alfred Voice&rdquo;. Match it to the voice, or
                        a female voice ends up introducing itself as Alfred.
                      </span>
                    </label>

                    <label className="flex items-start gap-2 text-sm text-stone-300 sm:pt-6">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={c.realtimeFallback}
                        onChange={(e) => set(n.id, { realtimeFallback: e.target.checked })}
                      />
                      <span>
                        Fall back to the standard stack if realtime fails
                        {/* Honesty over polish: the panel cannot do this mid-call yet, and a
                            checkbox that looks armed but is not is worse than no checkbox. */}
                        <span className="block text-xs text-stone-500">
                          Recorded, but not yet active mid-call — a realtime failure today ends
                          up on the standard stack only on the next call.
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                {lost.length > 0 && (
                  <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
                    <div className="flex items-center gap-2 text-xs font-medium text-stone-200 mb-1">
                      <AlertTriangle size={13} className="text-amber-500 shrink-0" />
                      This number will not be able to:
                    </div>
                    <ul className="text-xs text-stone-300 space-y-1">
                      {lost.map((l) => (
                        <li key={l.key}>• {l.label}{l.note ? ` — ${l.note}` : ''}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {dirty(n) && (
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={saving === n.id}
                      onClick={() => save(n)}
                    >
                      {saving === n.id ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
